import React, { useMemo } from 'react';
import { useBatteryStock, useChargerStock, useSales } from '../hooks/useDatabase';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import './dashboard.css';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const formatCurrency = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
const formatInt = (n) => Number(n || 0).toLocaleString('th-TH');

const StatCard = ({ icon, title, value, sub, href, onClick }) => {
  const Element = href ? 'a' : 'div';
  const props = href
    ? { href, onClick: (e) => { if (onClick) { e.preventDefault(); onClick(); } } }
    : { onClick };
  return (
    <Element
      className={`stat-card ${onClick ? 'clickable' : ''}`}
      {...props}
    >
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div className="stat-title">{title}</div>
        <div className="stat-value">{value}</div>
        {sub && <div className="stat-sub">{sub}</div>}
      </div>
    </Element>
  );
};

export default function DashboardPage({ onNavigate }) {
  const { batteryStock, loading: batteryLoading } = useBatteryStock();
  const { chargerStock, loading: chargerLoading } = useChargerStock();
  const { sales, loading: salesLoading } = useSales();

  const loading = batteryLoading || chargerLoading || salesLoading;

  const kpis = useMemo(() => {
    // Helper to safely get sale amount with multiple fallbacks
    const getSaleAmount = (s) => {
      const direct = s?.saleAmount ?? s?.total_amount;
      if (direct != null && !Number.isNaN(Number(direct))) return Number(direct);
      if (Array.isArray(s?.items)) {
        return s.items.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0);
      }
      return 0;
    };
    const batteries = batteryStock.length;
    const chargers = chargerStock.length;

    const thisMonth = new Date().getMonth();
    const thisYear = new Date().getFullYear();

    const totalSales = sales.length;
    // Use saleAmount from /api/sales; fallback to total_amount for legacy compatibility
    const totalRevenue = sales.reduce((sum, s) => sum + getSaleAmount(s), 0);

    const revenueThisMonth = sales
      .filter((s) => {
        const d = new Date(s.saleDate || s.created_at || s.updated_at || Date.now());
        return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
      })
      .reduce((sum, s) => sum + getSaleAmount(s), 0);

    return { batteries, chargers, totalSales, totalRevenue, revenueThisMonth };
  }, [batteryStock, chargerStock, sales]);

  const charts = useMemo(() => {
    // 6 เดือนล่าสุด
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.toLocaleString('th-TH', { month: 'short' })} ${d.getFullYear() + 543}`);
    }

    const monthKey = (d) => {
      const dd = new Date(d);
      return `${dd.getFullYear()}-${dd.getMonth()}`;
    };
    // เตรียม map ยอดขายต่อเดือน
    const monthMap = {};
    months.forEach((m, idx) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - idx), 1);
      monthMap[`${d.getFullYear()}-${d.getMonth()}`] = 0;
    });
    sales.forEach((s) => {
      const key = monthKey(s.saleDate || s.created_at || s.updated_at || Date.now());
      if (monthMap[key] !== undefined) {
        const amt = (() => {
          const direct = s?.saleAmount ?? s?.total_amount;
          if (direct != null && !Number.isNaN(Number(direct))) return Number(direct);
          if (Array.isArray(s?.items)) {
            return s.items.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0);
          }
          return 0;
        })();
        monthMap[key] += amt;
      }
    });
    const monthlyValues = Object.values(monthMap);

    const barData = {
      labels: months,
      datasets: [
        {
          label: 'รายได้ (บาท)',
          data: monthlyValues,
          backgroundColor: 'rgba(0, 102, 204, 0.25)',
          borderColor: '#0066CC',
          borderWidth: 2,
          borderRadius: 8,
          maxBarThickness: 36
        }
      ]
    };

    // คำนวณสัดส่วนสต็อกตามสถานะ - แยกตามประเภท
    const batteryStatus = {
      available: 0,    // พร้อมขาย
      sold: 0,         // ขายแล้ว
      pending: 0,      // ติดจอง
      damaged: 0       // เสียหาย
    };

    const chargerStatus = {
      available: 0,    // พร้อมขาย
      sold: 0,         // ขายแล้ว
      pending: 0,      // ติดจอง
      damaged: 0       // เสียหาย
    };

    // นับสถานะจากแบตเตอรี่
    batteryStock.forEach(item => {
      const status = item.status?.toLowerCase() || 'available';
      if (status === 'available' || status === 'พร้อมขาย') {
        batteryStatus.available++;
      } else if (status === 'sold' || status === 'ขายแล้ว') {
        batteryStatus.sold++;
      } else if (status === 'pending' || status === 'ติดจอง') {
        batteryStatus.pending++;
      } else if (status === 'damaged' || status === 'เสียหาย') {
        batteryStatus.damaged++;
      } else {
        batteryStatus.available++; // default เป็นพร้อมขาย
      }
    });

    // นับสถานะจากชาร์จเจอร์
    chargerStock.forEach(item => {
      const status = item.status?.toLowerCase() || 'available';
      if (status === 'available' || status === 'พร้อมขาย') {
        chargerStatus.available++;
      } else if (status === 'sold' || status === 'ขายแล้ว') {
        chargerStatus.sold++;
      } else if (status === 'pending' || status === 'ติดจอง') {
        chargerStatus.pending++;
      } else if (status === 'damaged' || status === 'เสียหาย') {
        chargerStatus.damaged++;
      } else {
        chargerStatus.available++; // default เป็นพร้อมขาย
      }
    });

    const batteryDoughnutData = {
      labels: ['พร้อมขาย', 'ขายแล้ว', 'ติดจอง', 'เสียหาย'],
      datasets: [
        {
          data: [batteryStatus.available, batteryStatus.sold, batteryStatus.pending, batteryStatus.damaged],
          backgroundColor: ['#28a745', '#6c757d', '#ffc107', '#dc3545'],
          borderColor: ['#ffffff', '#ffffff', '#ffffff', '#ffffff'],
          borderWidth: 2,
          hoverOffset: 8
        }
      ]
    };

    const chargerDoughnutData = {
      labels: ['พร้อมขาย', 'ขายแล้ว', 'ติดจอง', 'เสียหาย'],
      datasets: [
        {
          data: [chargerStatus.available, chargerStatus.sold, chargerStatus.pending, chargerStatus.damaged],
          backgroundColor: ['#28a745', '#6c757d', '#ffc107', '#dc3545'],
          borderColor: ['#ffffff', '#ffffff', '#ffffff', '#ffffff'],
          borderWidth: 2,
          hoverOffset: 8
        }
      ]
    };

    return { barData, batteryDoughnutData, chargerDoughnutData };
  }, [sales, batteryStock, chargerStock]);

  return (
    <div className="dashboard">
      <div className="dashboard-hero">
        <div>
          <h2>แดชบอร์ด – ระบบจัดการแบตเตอรี่</h2>
          <p className="hero-sub">ภาพรวมสถานะสต็อกและยอดขายล่าสุด</p>
        </div>
      </div>

      {loading ? (
        <div className="skeleton-grid">
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton-card" />)}
        </div>
      ) : (
        <>
          <div className="kpi-grid">
            <StatCard
              icon={<i className="fas fa-battery-full"></i>}
              title="แบตเตอรี่ทั้งหมด"
              value={formatInt(kpis.batteries)}
              sub="พร้อมวิเคราะห์และติดตาม"
              href="/battery-stock"
              onClick={() => onNavigate && onNavigate('batteryStock')}
            />
            <StatCard
              icon={<i className="fas fa-plug"></i>}
              title="ชาร์จเจอร์ทั้งหมด"
              value={formatInt(kpis.chargers)}
              sub="รวมทุกสถานะในระบบ"
              href="/charger-stock"
              onClick={() => onNavigate && onNavigate('chargerStock')}
            />
            <StatCard
              icon={<i className="fas fa-shopping-cart"></i>}
              title="ยอดขายเดือนนี้"
              value={formatCurrency(kpis.revenueThisMonth)}
              sub={`${kpis.totalSales} ดีลทั้งหมด`}
            />
            <StatCard
              icon={<i className="fas fa-coins"></i>}
              title="รายได้สะสม"
              value={formatCurrency(kpis.totalRevenue)}
              sub="ตั้งแต่เริ่มใช้งานระบบ"
            />
          </div>

          <div className="chart-grid">
            <div className="chart-card">
              <div className="chart-title"><i className="fas fa-chart-bar me-2" />รายได้ 6 เดือนล่าสุด</div>
              <div className="chart-container">
                <Bar
                  data={charts.barData}
                  options={{
                    plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        ticks: {
                          callback: (v) => Number(v).toLocaleString('th-TH')
                        },
                        grid: { color: 'rgba(0,0,0,0.06)' }
                      },
                      x: { grid: { display: false } }
                    }
                  }}
                />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-title">
                <i className="fas fa-battery-full me-2" />
                สัดส่วนสต็อกแบตเตอรี่
              </div>
              <div className="chart-container">
                <Doughnut
                  data={charts.batteryDoughnutData}
                  options={{
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          usePointStyle: true,
                          padding: 12,
                          font: { size: 11 }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const label = context.label || '';
                            const value = context.parsed;
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            return `${label}: ${value} ชิ้น (${percentage}%)`;
                          }
                        }
                      }
                    },
                    cutout: '60%',
                    responsive: true,
                    maintainAspectRatio: false,   // ให้ปรับตามขนาดคอนเทนเนอร์
                    resizeDelay: 150
                  }}
                  style={{ maxHeight: 360 }}
                />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-title">
                <i className="fas fa-plug me-2" />
                สัดส่วนสต็อกชาร์จเจอร์
              </div>
              <div className="chart-container">
                <Doughnut
                  data={charts.chargerDoughnutData}
                  options={{
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          usePointStyle: true,
                          padding: 12,
                          font: { size: 11 }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const label = context.label || '';
                            const value = context.parsed;
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            return `${label}: ${value} ชิ้น (${percentage}%)`;
                          }
                        }
                      }
                    },
                    cutout: '60%',
                    responsive: true,
                    maintainAspectRatio: false,
                    resizeDelay: 150
                  }}
                  style={{ maxHeight: 360 }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}